import { LOGO_DURATION } from "./logoSequence";

/**
 * The intro's soundtrack, generated live with Web Audio (no audio files):
 * an ambient pad, heartbeat thumps, a chime when the mark forms, whooshes
 * on each scene, a note per step, and the fireworks.
 */

const ECG_BEATS = [1.2, 1.95, 2.7, 3.45];
const HEART_BEATS = [5.25, 5.8, 6.35];
const SCENE_STARTS = [2.7, 6.7, 14.7, 22.7, 30.7, 38.8];
const STEP_NOTES: [number, number][] = [
  [7.1, 523.3],
  [15.1, 587.3],
  [23.1, 659.3],
  [31.1, 784.0],
];
const CHORDS = [
  [261.6, 329.6, 392.0, 493.9],
  [220.0, 261.6, 329.6, 392.0],
  [174.6, 220.0, 261.6, 329.6],
  [196.0, 246.9, 293.7, 392.0],
];

type AudioContextClass = typeof AudioContext;

export interface IntroAudio {
  stop: () => void;
}

/**
 * Start the soundtrack `offset` seconds into the intro. `skipLogo` when the
 * logo phase isn't played (reduced motion). `burstTimes` are the fireworks'
 * seconds into the steps phase. `onBlocked` fires if the browser keeps the
 * sound suspended until the visitor interacts.
 */
export const startIntroAudio = (
  offset: number,
  skipLogo: boolean,
  burstTimes: number[],
  onBlocked: () => void,
): IntroAudio | null => {
  const AC: AudioContextClass | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextClass })
      .webkitAudioContext;
  if (!AC) return null;

  const ctx = new AC();
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    ctx.close().catch(() => undefined);
  };

  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);
  const tL = ctx.currentTime + 0.08 - offset; // logo sequence start
  const late = (t: number) => t < ctx.currentTime + 0.01; // its moment has passed
  const t0 = tL + (skipLogo ? 0 : LOGO_DURATION); // steps phase start

  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = noiseBuf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

  const env = (
    g: GainNode,
    t: number,
    attack: number,
    hold: number,
    release: number,
    peak: number,
  ) => {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.setValueAtTime(peak, t + attack + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + hold + release);
  };
  const tone = (
    freq: number,
    t: number,
    dur: number,
    type: OscillatorType,
    vol: number,
  ) => {
    if (late(t)) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    env(g, t, 0.01, 0, dur, vol);
    o.connect(g);
    g.connect(master);
    o.start(t);
    o.stop(t + dur + 0.1);
  };
  const noise = (
    t: number,
    dur: number,
    f1: number,
    f2: number,
    q: number,
    vol: number,
    kind: BiquadFilterType = "bandpass",
  ) => {
    if (late(t)) return;
    const s = ctx.createBufferSource();
    s.buffer = noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = kind;
    f.Q.value = q;
    f.frequency.setValueAtTime(f1, t);
    f.frequency.exponentialRampToValueAtTime(f2, t + dur);
    const g = ctx.createGain();
    env(g, t, dur * 0.35, 0, dur * 0.65, vol);
    s.connect(f);
    f.connect(g);
    g.connect(master);
    s.start(t);
    s.stop(t + dur + 0.05);
  };

  // Ambient pad from the very start.
  const padBus = ctx.createBiquadFilter();
  padBus.type = "lowpass";
  padBus.frequency.value = 1100;
  const padGain = ctx.createGain();
  padGain.gain.value = 1;
  padBus.connect(padGain);
  padGain.connect(master);
  const bars = Math.ceil((t0 - tL + 44) / 4);
  for (let bar = 0; bar < bars; bar++) {
    const t = tL + bar * 4;
    if (late(t)) continue;
    CHORDS[bar % 4].forEach((fq, j) => {
      [-4, 4].forEach((cents) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = j === 0 ? "triangle" : "sine";
        o.frequency.value = fq * (j === 0 ? 0.5 : 1);
        o.detune.value = cents;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.linearRampToValueAtTime(0.022, t + 1.4);
        g.gain.linearRampToValueAtTime(0.018, t + 3.6);
        g.gain.linearRampToValueAtTime(0.0001, t + 4.8);
        o.connect(g);
        g.connect(padBus);
        o.start(t);
        o.stop(t + 5);
      });
    });
  }
  padGain.gain.setValueAtTime(1, Math.max(ctx.currentTime, t0 + 39));
  padGain.gain.linearRampToValueAtTime(
    0.0001,
    Math.max(ctx.currentTime + 0.5, t0 + 42),
  );

  if (!skipLogo) {
    // The line sweeps in.
    noise(tL + 0.2, 0.7, 300, 2400, 0.7, 0.06);
    // Heartbeat on the line.
    ECG_BEATS.forEach((s) => {
      tone(70, tL + s - 0.03, 0.24, "sine", 0.22);
      tone(140, tL + s - 0.03, 0.12, "sine", 0.05);
      tone(62, tL + s + 0.17, 0.2, "sine", 0.12);
    });
    // The line curls into a heart: a soft rising glide and a warm swell.
    if (!late(tL + 3.9)) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(330, tL + 3.9);
      o.frequency.exponentialRampToValueAtTime(523.3, tL + 5.0);
      env(g, tL + 3.9, 0.3, 0.6, 0.6, 0.03);
      o.connect(g);
      g.connect(master);
      o.start(tL + 3.9);
      o.stop(tL + 5.6);
    }
    tone(523.3, tL + 4.9, 1.8, "sine", 0.03);
    tone(659.3, tL + 5.0, 1.7, "sine", 0.025);
    // The heart pulses.
    HEART_BEATS.forEach((s) => {
      tone(66, tL + s - 0.03, 0.28, "sine", 0.26);
      tone(132, tL + s - 0.03, 0.14, "sine", 0.06);
      tone(58, tL + s + 0.17, 0.22, "sine", 0.14);
    });
    // The heart turns and reshapes into the paperclip.
    noise(tL + 6.7, 0.7, 600, 1800, 0.9, 0.05);
    tone(392.0, tL + 7.4, 1.2, "triangle", 0.025);
    tone(587.3, tL + 7.8, 1.0, "triangle", 0.02);
    // The mark fills in: a shimmering chime.
    [784, 988, 1175, 1568].forEach((f, i) =>
      tone(f, tL + 8.5 + i * 0.1, 1.4, "sine", 0.05),
    );
    // The name writes in: a whoosh.
    noise(tL + 9.15, 0.9, 500, 3600, 0.8, 0.08);
  }

  SCENE_STARTS.forEach((s) => noise(t0 + s, 0.9, 400, 3200, 0.8, 0.09));
  STEP_NOTES.forEach(([s, f]) => {
    tone(f, t0 + s, 0.9, "triangle", 0.09);
    tone(f * 2, t0 + s + 0.02, 0.6, "sine", 0.03);
  });

  // Fireworks: launch whistle, boom, crackle.
  burstTimes.forEach((s) => {
    const ls = Math.max(0, s - 0.55);
    if (!late(t0 + ls)) {
      const lw = ctx.createOscillator();
      const lg = ctx.createGain();
      lw.type = "sine";
      lw.frequency.setValueAtTime(700, t0 + ls);
      lw.frequency.exponentialRampToValueAtTime(1900, t0 + s - 0.02);
      env(lg, t0 + ls, 0.05, Math.max(0.05, s - ls - 0.15), 0.1, 0.012);
      lw.connect(lg);
      lg.connect(master);
      lw.start(t0 + ls);
      lw.stop(t0 + s);
    }
    noise(t0 + s, 0.7, 900, 120, 0.7, 0.22, "lowpass");
    for (let k = 0; k < 9; k++) {
      noise(
        t0 + s + 0.15 + Math.random() * 0.7,
        0.05,
        3000 + Math.random() * 3000,
        2500,
        3,
        0.05,
      );
    }
  });
  [523.3, 659.3, 784.0, 1046.5].forEach((f) =>
    tone(f, t0 + 39.0, 2.4, "sine", 0.035),
  );

  // Browsers block sound until the visitor interacts.
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => undefined);
    setTimeout(() => {
      if (!stopped && ctx.state !== "running") {
        stop();
        onBlocked();
      }
    }, 300);
  }

  return { stop };
};
